import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Alert, 
  CircularProgress, 
  TablePagination, 
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CalculateIcon from '@mui/icons-material/Calculate';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';

import * as XLSX from 'xlsx';
import api from '../../api';

// Components
import CorrectionDashboard from './CorrectionDashboard';
import CorrectionReport from './CorrectionReport';
import AttendanceReportDashboard from './AttendanceReportDashboard';
import AttendanceReportList from './AttendanceReportList';
import PayDaysReport from './PayDaysReport';
import AttendanceOverdueReportView from './AttendanceOverdueReportView';
import AttendanceImport from './AttendanceImport';
import AttendanceImportReport from './AttendanceImportReport';

const AttendanceMaster = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  // Navigation state - flat like Dashboard
  const [activeItem, setActiveItem] = useState('attendance_dashboard');
  
  // Data fetching states
  const [empCode, setEmpCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleItemClick = (id) => {
    setActiveItem(id);
    // Reset data when switching
    setData([]);
    setError('');
    setEmpCode('');
  };

  const handleFetch = async () => {
    if (!empCode || !startDate || !endDate) {
      setError("Please fill all fields (Employee Code, Start Date, End Date).");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/attendance-master/fetch', {
        source: activeItem, 
        employee_code: empCode, 
        start_date: startDate, 
        end_date: endDate
      });
      setData(response.data.data);
      if(response.data.data.length === 0) setError("No attendance records found for this date range.");
      setPage(0);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch attendance records.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance_Logs");
    XLSX.writeFile(workbook, `${activeItem}_Attendance_${empCode}_${startDate}_to_${endDate}.xlsx`);
  };

  // NavGroupHeader - EXACTLY LIKE DASHBOARD
  const NavGroupHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  // NavItem - EXACTLY LIKE DASHBOARD
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeItem === id} 
        onClick={() => handleItemClick(id)}
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' } 
          },
          '&.Mui-selected .MuiListItemIcon-root': { color: 'white' }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeItem === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeItem === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - IDENTICAL TO DASHBOARD */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <NavGroupHeader title="ATTENDANCE REPORT" />
          <NavItem id="attendance_dashboard" label="Dashboard" icon={<DashboardIcon fontSize="small" />} />
          <NavItem id="attendance_detailed_report" label="Detailed Report" icon={<DescriptionIcon fontSize="small" />} />
          <NavItem id="attendance_overdue" label="Overdue" icon={<EventBusyIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <NavGroupHeader title="CORRECTIONS" />
          <NavItem id="correction_dashboard" label="Dashboard" icon={<DashboardIcon fontSize="small" />} />
          <NavItem id="correction_report" label="Report" icon={<DescriptionIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <NavGroupHeader title="IMPORT ATTENDANCE" />
          <NavItem id="import_attendance" label="Import Data" icon={<UploadFileIcon fontSize="small" />} />
          <NavItem id="import_audit_report" label="Report" icon={<HistoryIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <NavGroupHeader title="PAY DAYS" />
          <NavItem id="pay_days" label="Pay Days" icon={<CalculateIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <NavGroupHeader title="DATA SOURCE" />
          <NavItem id="tracking" label="Tracking Tool" icon={<LocationSearchingIcon fontSize="small" />} />
          <NavItem id="biometric" label="Biometric DB" icon={<FingerprintIcon fontSize="small" />} />

        </List>
      </Box>

      {/* RIGHT CONTENT AREA - IDENTICAL TO DASHBOARD */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        width: '100%', 
        bgcolor: '#fbfbfb', 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        
        {/* ATTENDANCE REPORT SECTION */}
        {activeItem === 'attendance_dashboard' && <AttendanceReportDashboard />}
        {activeItem === 'attendance_detailed_report' && <AttendanceReportList />}
        {activeItem === 'attendance_overdue' && <AttendanceOverdueReportView />}

        {/* CORRECTIONS SECTION */}
        {activeItem === 'correction_dashboard' && <CorrectionDashboard />}
        {activeItem === 'correction_report' && <CorrectionReport />}

        {/* IMPORT ATTENDANCE SECTION */}
        {activeItem === 'import_attendance' && <AttendanceImport />}
        {activeItem === 'import_audit_report' && <AttendanceImportReport />}

        {/* PAY DAYS SECTION */}
        {activeItem === 'pay_days' && <PayDaysReport />}

        {/* DATA SOURCE SECTION */}
        {activeItem === 'tracking' && (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, bgcolor: '#ffffff', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <LocationSearchingIcon sx={{ color: '#1976d2', mr: 1.5, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10394f' }}>
                Query Tracking Tool Database
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#ffffff', mb: 4, border: '1px solid #e0e0e0' }} elevation={0}>
              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField 
                  size="small" 
                  label="Employee Code" 
                  placeholder="e.g., TG13546" 
                  value={empCode} 
                  onChange={(e) => setEmpCode(e.target.value)} 
                  sx={{ minWidth: 200 }} 
                />
                <TextField 
                  size="small" 
                  label="Start Date" 
                  type="date" 
                  InputLabelProps={{ shrink: true }} 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
                <TextField 
                  size="small" 
                  label="End Date" 
                  type="date" 
                  InputLabelProps={{ shrink: true }} 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)} 
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<SearchIcon />} 
                  onClick={handleFetch} 
                  disabled={loading} 
                  disableElevation
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Fetch Logs'}
                </Button>
              </Box>
            </Paper>

            {data.length > 0 && (
              <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', overflow: 'hidden', border: '1px solid #e0e0e0' }} elevation={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', bgcolor: '#f9fafb' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="#444">Results ({data.length} records)</Typography>
                  <Button variant="outlined" color="success" size="small" startIcon={<DownloadIcon />} onClick={exportToExcel}>
                    Export to Excel
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Attendance Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>In Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Out Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ color: '#444' }}>{row.AttendanceDate}</TableCell>
                          <TableCell sx={{ color: '#2e7d32', fontWeight: 500 }}>{row.InTime}</TableCell>
                          <TableCell sx={{ color: '#c62828', fontWeight: 500 }}>{row.OutTime}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination 
                  component="div" 
                  count={data.length} 
                  rowsPerPage={rowsPerPage} 
                  page={page} 
                  onPageChange={(e, newPage) => setPage(newPage)} 
                  onRowsPerPageChange={(e) => { 
                    setRowsPerPage(parseInt(e.target.value, 10)); 
                    setPage(0); 
                  }} 
                  sx={{ borderTop: '1px solid #eee' }} 
                />
              </Paper>
            )}
          </Box>
        )}

        {activeItem === 'biometric' && (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, bgcolor: '#ffffff', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <FingerprintIcon sx={{ color: '#1976d2', mr: 1.5, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10394f' }}>
                Query Biometric Database
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#ffffff', mb: 4, border: '1px solid #e0e0e0' }} elevation={0}>
              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField 
                  size="small" 
                  label="Employee Code" 
                  placeholder="e.g., 13546" 
                  value={empCode} 
                  onChange={(e) => setEmpCode(e.target.value)} 
                  sx={{ minWidth: 200 }} 
                />
                <TextField 
                  size="small" 
                  label="Start Date" 
                  type="date" 
                  InputLabelProps={{ shrink: true }} 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
                <TextField 
                  size="small" 
                  label="End Date" 
                  type="date" 
                  InputLabelProps={{ shrink: true }} 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)} 
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<SearchIcon />} 
                  onClick={handleFetch} 
                  disabled={loading} 
                  disableElevation
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Fetch Logs'}
                </Button>
              </Box>
            </Paper>

            {data.length > 0 && (
              <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', overflow: 'hidden', border: '1px solid #e0e0e0' }} elevation={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', bgcolor: '#f9fafb' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="#444">Results ({data.length} records)</Typography>
                  <Button variant="outlined" color="success" size="small" startIcon={<DownloadIcon />} onClick={exportToExcel}>
                    Export to Excel
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Attendance Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>In Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Out Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ color: '#444' }}>{row.AttendanceDate}</TableCell>
                          <TableCell sx={{ color: '#2e7d32', fontWeight: 500 }}>{row.InTime}</TableCell>
                          <TableCell sx={{ color: '#c62828', fontWeight: 500 }}>{row.OutTime}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination 
                  component="div" 
                  count={data.length} 
                  rowsPerPage={rowsPerPage} 
                  page={page} 
                  onPageChange={(e, newPage) => setPage(newPage)} 
                  onRowsPerPageChange={(e) => { 
                    setRowsPerPage(parseInt(e.target.value, 10)); 
                    setPage(0); 
                  }} 
                  sx={{ borderTop: '1px solid #eee' }} 
                />
              </Paper>
            )}
          </Box>
        )}

      </Box>
    </Paper>
  );
};

export default AttendanceMaster;